import { Link, useNavigate } from 'react-router';
import { SEVERITY_ORDER, type Dashboard, type Finding } from '@ametica/shared';
import { useDashboard } from '../api/findings';
import { PageTitle } from '../layout/PageTitle';
import { Icon } from '../components/Icon';
import { UptimeSoonCard } from '../features/uptime/UptimeSoonCard';
import { SeverityBadge } from '../features/findings/SeverityBadge';
import { CoveragePanel } from '../features/coverage/CoveragePanel';
import { SEVERITY, catalogFreshness, pluralFindings, pluralSites, scansFreshness, type FreshnessTone } from '../features/findings/severity';
import { formatDateTime, timeAgo } from '../lib/time';

const TONE: Record<FreshnessTone, string> = { ok: 'bg-ok', stale: 'bg-sev-medium', bad: 'bg-sev-critical', none: 'bg-ghost' };

/** Prehľad podľa design/_app.js (V.dashboard): dlaždice, posledné zraniteľnosti, závažnosť, aktuálnosť dát, pokrytie katalógom, miesto pre dostupnosť. */
export function DashboardPage() {
  const dash = useDashboard();

  if (dash.isPending) return <PageTitle title="Prehľad" />;
  if (dash.isError || !dash.data) {
    return (
      <>
        <PageTitle title="Prehľad" />
        <div className="card p-6 text-sev-critical">Prehľad sa nepodarilo načítať: {dash.error?.message}</div>
      </>
    );
  }
  const d = dash.data;

  return (
    <>
      <PageTitle title="Prehľad" sub={`${d.sites.total} ${d.sites.total === 1 ? 'stránka' : d.sites.total < 5 ? 'stránky' : 'stránok'} pod dohľadom`} />

      <div className="grid4">
        <Tile label="Sledované stránky" value={d.sites.total} foot={sitesFoot(d)} footTone={d.sites.unknown > 0 ? 'text-sev-medium' : undefined} />
        <Tile label="Otvorené zraniteľnosti" value={d.findings.open} foot={d.findings.newLast24h > 0 ? `${d.findings.newLast24h} pribudl${d.findings.newLast24h === 1 ? 'a' : 'o'} za posledný deň` : 'žiadna nová za posledný deň'} />
        <Tile
          label="Kritické"
          value={d.findings.critical}
          tone={d.findings.critical > 0 ? 'text-sev-critical' : undefined}
          foot={d.findings.critical > 0 ? `na ${d.findings.criticalSites} ${pluralSites(d.findings.criticalSites)}` : 'žiadna kritická'}
          footTone={d.findings.critical > 0 ? 'text-sev-critical' : undefined}
        />
        <Tile
          label="Opraviteľné hneď"
          value={d.findings.fixable}
          foot={d.findings.open > 0 ? `z ${d.findings.open} už existuje patch` : 'nie je čo opravovať'}
          footTone={d.findings.fixable > 0 ? 'text-ok' : undefined}
        />
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,380px)]">
        <div className="flex min-w-0 flex-col gap-6">
          <RecentFindings recent={d.recent} />
          <UptimeSoonCard />
        </div>
        <div className="flex flex-col gap-6">
          <BySeverity d={d} />
          <FreshnessCard d={d} />
          <CoveragePanel />
        </div>
      </div>
    </>
  );
}

function sitesFoot(d: Dashboard): string {
  if (d.sites.total === 0) return 'zatiaľ žiadna — pridaj prvú';
  const parts = [`${d.sites.reportedToday} sa dnes ozval${d.sites.reportedToday === 1 ? 'a' : 'o'}`];
  if (d.sites.unknown > 0) parts.push(`${d.sites.unknown} bez reportu`);
  return parts.join(' · ');
}

function Tile({ label, value, foot, tone, footTone }: { label: string; value: number; foot: string; tone?: string; footTone?: string }) {
  return (
    <div className="tile">
      <span className="tile-l">{label}</span>
      <span className={`tile-v ${tone ?? ''}`}>{value}</span>
      <span className={`tile-f ${footTone ?? ''}`}>{foot}</span>
    </div>
  );
}

function RecentFindings({ recent }: { recent: Finding[] }) {
  const navigate = useNavigate();
  return (
    <div className="card">
      <div className="card-h">
        <span className="card-t">Posledné zraniteľnosti</span>
        <Link to="/vulnerabilities" className="btn btn-g h-[30px] text-xs">Všetky →</Link>
      </div>
      <div className="tblwrap">
        <table className="tbl">
          <tbody>
            {recent.length === 0 && <tr><td className="text-faint">Žiadny otvorený nález. Nula je čistá len pre stránky, ktoré sa ozvali — pozri dlaždicu vľavo.</td></tr>}
            {recent.map((f) => (
              <tr key={f.id} className="click" onClick={() => navigate(`/vulnerabilities/${f.id}`)}>
                <td className="w-[140px]"><SeverityBadge severity={f.severity} /></td>
                <td>
                  <div>{f.title}</div>
                  <div className="sub">{f.component.slug} {f.installedVersion} · {f.cve ?? 'bez CVE'}{f.patchedIn ? ` · oprava ${f.patchedIn}` : ''}</div>
                </td>
                <td className="mono w-[150px] text-dim">{f.site.host}</td>
                <td className="w-[110px] text-right text-[11.5px] text-faint" title={formatDateTime(f.firstDetectedAt)}>{timeAgo(f.firstDetectedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BySeverity({ d }: { d: Dashboard }) {
  return (
    <div className="card flex flex-col gap-3.5 px-5 py-[18px]">
      <span className="card-t">Podľa závažnosti</span>
      <div>
        {SEVERITY_ORDER.map((sev) => (
          <div key={sev} className="kv border-b border-[#242424] py-[7px] last:border-b-0">
            <span className="flex items-center gap-[9px]">
              <span className={`sevsw ${SEVERITY[sev].bg}`} />
              <span className="text-[12.5px]">{SEVERITY[sev].label}</span>
            </span>
            <span className="mono text-[12.5px] text-dim">{d.bySeverity[sev]}</span>
          </div>
        ))}
      </div>
      <span className="hint">{d.findings.open} {pluralFindings(d.findings.open)} spolu · nepotvrdená = bez CVSS, nie neškodná</span>
    </div>
  );
}

/** FeedSyncRun a ScanRun sú dve rôzne aktuálnosti — každá má vlastný riadok, vlastný stav a vlastný cieľ odkazu. */
function FreshnessCard({ d }: { d: Dashboard }) {
  const catalog = catalogFreshness(d);
  const scans = scansFreshness(d);
  const nf = new Intl.NumberFormat('sk');
  return (
    <div className="card flex flex-col gap-3.5 px-5 py-[18px]">
      <span className="card-t">Aktuálnosť dát</span>
      <FreshRow tone={catalog.tone} label={catalog.label} detail={`${nf.format(d.catalog.total)} záznamov v katalógu`} to="/settings" hint="Wordfence feed — míňa kvótu" />
      <FreshRow tone={scans.tone} label={scans.label} detail={d.scans.lastRunAt ? `posledný sken ${formatDateTime(d.scans.lastRunAt)}` : 'zatiaľ žiadny sken'} to="/sites" hint="inventár zo stránok — každých 6 h, bez kvóty" />
    </div>
  );
}

function FreshRow({ tone, label, detail, to, hint }: { tone: FreshnessTone; label: string; detail: string; to: string; hint: string }) {
  return (
    <Link to={to} className="flex items-start gap-2.5 rounded-[6px] py-1 text-ink hover:text-ink">
      <span className={`dotst mt-[6px] ${TONE[tone]}`} />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[12.5px]">{label}</span>
        <span className="mono text-[11px] text-faint">{detail}</span>
        <span className="text-[11px] text-ghost">{hint}</span>
      </span>
      <Icon name="chev" size={14} className="ml-auto mt-1 shrink-0 text-ghost" />
    </Link>
  );
}
